import type { ContactLanguageSource, SupportedLanguage } from "./contracts";

/** The bot's default: a contact with no preference is spoken to in English. */
export const DEFAULT_LANGUAGE = "en";

/**
 * Vendored registry of the cross-repo `echo-language.v1` fixture (plan
 * 2026-09-21 §4.2): wave A (it es fr pt nl de), wave B (hr cs sq pl ro sr),
 * wave C (bg uk ru el) and wave D (bs mk sv da hu sl, 2026-10-13). The bot's `supported_languages` list is
 * authoritative for what an operator may *pick*; this copy only supplies
 * labels when that list has not loaded (list chips, Echo badges, the contact
 * and Echo list filters, the voice card) and as the selector's fallback when
 * the bot returns an empty list.
 */
export const VENDORED_LANGUAGES: readonly SupportedLanguage[] = [
  { code: "en", english_label: "English", endonym: "English" },
  // Wave A
  { code: "it", english_label: "Italian", endonym: "italiano" },
  { code: "es", english_label: "Spanish", endonym: "español" },
  { code: "fr", english_label: "French", endonym: "français" },
  { code: "pt", english_label: "Portuguese", endonym: "português" },
  { code: "nl", english_label: "Dutch", endonym: "Nederlands" },
  { code: "de", english_label: "German", endonym: "Deutsch" },
  // Wave B (Latin-extended)
  { code: "hr", english_label: "Croatian", endonym: "hrvatski" },
  { code: "cs", english_label: "Czech", endonym: "čeština" },
  { code: "sq", english_label: "Albanian", endonym: "shqip" },
  { code: "pl", english_label: "Polish", endonym: "polski" },
  { code: "ro", english_label: "Romanian", endonym: "română" },
  { code: "sr", english_label: "Serbian", endonym: "srpski" },
  // Wave C (Cyrillic / Greek)
  { code: "bg", english_label: "Bulgarian", endonym: "български" },
  { code: "uk", english_label: "Ukrainian", endonym: "українська" },
  { code: "ru", english_label: "Russian", endonym: "русский" },
  { code: "el", english_label: "Greek", endonym: "ελληνικά" },
  // Wave D (60-day production demand, 2026-10-13)
  { code: "bs", english_label: "Bosnian", endonym: "bosanski" },
  { code: "mk", english_label: "Macedonian", endonym: "македонски" },
  { code: "sv", english_label: "Swedish", endonym: "svenska" },
  { code: "da", english_label: "Danish", endonym: "dansk" },
  { code: "hu", english_label: "Hungarian", endonym: "magyar" },
  { code: "sl", english_label: "Slovenian", endonym: "slovenščina" },
];

/** @deprecated wave-A name kept for older imports; the list now carries every wave. */
export const WAVE_A_LANGUAGES = VENDORED_LANGUAGES;

/** Lowercase primary subtag: "IT" -> "it", "pt-BR" -> "pt". */
export function normalizeLanguageCode(
  code: string | null | undefined,
): string | null {
  const trimmed = String(code ?? "").trim();
  if (!trimmed) return null;
  return trimmed.split(/[-_]/)[0].toLowerCase();
}

/** True for null, empty, "en", "EN", "en-GB"…: the bot's default English. */
export function isDefaultLanguage(code: string | null | undefined): boolean {
  const normalized = normalizeLanguageCode(code);
  return normalized === null || normalized === DEFAULT_LANGUAGE;
}

/**
 * Label for a language code, preferring the bot's live list and falling back
 * to the vendored copy. Unknown codes degrade to the upper-cased code so a
 * contact on a language the CRM has not vendored yet still gets a legible chip.
 */
export function languageLabel(
  code: string | null | undefined,
  supported?: readonly SupportedLanguage[] | null,
): SupportedLanguage {
  const normalized = normalizeLanguageCode(code) ?? DEFAULT_LANGUAGE;
  const match =
    supported?.find((language) => language.code === normalized) ??
    VENDORED_LANGUAGES.find((language) => language.code === normalized);
  if (match) return match;
  const upper = normalized.toUpperCase();
  return { code: normalized, english_label: upper, endonym: upper };
}

/** "italiano · Italian" — endonym first, English second, once when equal. */
export function formatLanguage(language: SupportedLanguage): string {
  return language.endonym === language.english_label
    ? language.endonym
    : `${language.endonym} · ${language.english_label}`;
}

const SOURCE_LABELS: Record<string, string> = {
  user_confirmed: "Confirmed by the contact",
  user_requested: "Requested by the contact",
  crm_admin: "Set from the CRM",
  felix_tool: "Set by Felix",
  import: "Imported",
};

/** Operator-facing label for a `source` value; unknown values are humanised. */
export function languageSourceLabel(
  source: ContactLanguageSource | null | undefined,
): string | null {
  const value = String(source ?? "").trim();
  if (!value) return null;
  return SOURCE_LABELS[value] ?? value.replaceAll("_", " ");
}
